import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CreditCard, ArrowRight, Package, Lock, Clock, Euro, ExternalLink } from "lucide-react";

export default function StudentPayments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [requests, setRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(null);
  const [loadingPackages, setLoadingPackages] = useState(true);

  const coachId = user?.coach_id;

  useEffect(() => {
    if (!coachId) {
      setLoadingPackages(false);
      return;
    }

    const load = async () => {
      setLoadingPackages(true);
      try {
        const [pkgRes, reqRes, histRes] = await Promise.all([
          api.get("/coach-packages"),
          api.get("/payments/requests"),
          api.get("/payments/history"),
        ]);
        setPackages(pkgRes.data?.packages || []);
        setRequests((reqRes.data || []).filter((r) => r.status === "pending" && r.payable));
        setHistory(histRes.data || []);
      } catch {
        toast.error("Erreur de chargement des offres");
      } finally {
        setLoadingPackages(false);
      }
    };

    load();
  }, [coachId]);

  const handleBuy = async (packageId) => {
    setLoadingPkg(packageId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de paiement");
      setLoadingPkg(null);
    }
  };

  const handlePayRequest = async (token) => {
    setLoadingRequest(token);
    try {
      const { data } = await api.post(`/payments/requests/public/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de paiement");
      setLoadingRequest(null);
    }
  };

  if (!coachId) {
    return (
      <div data-testid="student-payments-page">
        <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Paiements</h1>
        <p className="text-slate-500 mt-1">Choisissez votre offre</p>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-2">Paiements verrouillés</h2>
          <p className="text-slate-500 mt-2 text-center max-w-sm">Vous devez d'abord ajouter un coach pour accéder aux offres et effectuer des paiements.</p>
          <Button onClick={() => navigate("/student/dashboard")} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full mt-6" data-testid="go-to-dashboard-btn">
            Ajouter un coach
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="student-payments-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Paiements</h1>
      <p className="text-slate-500 mt-1">Forfaits et demandes de votre coach</p>

      {requests.length > 0 && (
        <div className="mt-6 space-y-3">
          <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Euro className="w-5 h-5 text-amber-600" /> Demandes en attente
          </h2>
          {requests.map((req) => (
            <Card key={req.request_id} className="border-amber-200 bg-amber-50/40">
              <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-slate-900">{req.label}</p>
                  {req.description && <p className="text-sm text-slate-500 mt-1">{req.description}</p>}
                  <p className="text-xs text-slate-400 mt-1">De {req.coach_name}</p>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-2xl font-bold text-slate-900">{Number(req.amount).toFixed(2)}€</p>
                  <Button
                    onClick={() => handlePayRequest(req.token)}
                    disabled={loadingRequest === req.token}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                  >
                    {loadingRequest === req.token ? "..." : <>Payer <ArrowRight className="w-4 h-4 ml-1" /></>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <h2 className="font-heading text-lg font-semibold text-slate-900 mt-8 mb-4">Forfaits de mon coach</h2>

      {loadingPackages ? (
        <div className="text-center text-slate-500 py-10">Chargement des offres...</div>
      ) : packages.length === 0 ? (
        <Card className="border-dashed border-slate-200">
          <CardContent className="p-10 text-center">
            <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">Aucun forfait publié par votre coach</p>
            <p className="text-sm text-slate-400 mt-1">Votre coach peut vous envoyer une demande de paiement personnalisée.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {packages.map((pkg) => (
            <Card key={pkg.package_id} className="border-slate-100 hover:shadow-md transition-shadow" data-testid={`package-${pkg.package_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{pkg.name}</h3>
                    {pkg.description && <p className="text-sm text-slate-500 mt-1">{pkg.description}</p>}
                    {pkg.duration && (
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {pkg.duration} jours
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-2xl font-bold text-slate-900">{Number(pkg.amount).toFixed(2)}€</p>
                      <Button
                        onClick={() => handleBuy(pkg.package_id)}
                        disabled={loadingPkg === pkg.package_id}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                        data-testid={`buy-${pkg.package_id}-btn`}
                      >
                        {loadingPkg === pkg.package_id ? "..." : <>Acheter <ArrowRight className="w-4 h-4 ml-1" /></>}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="border-slate-100 mt-8">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="w-5 h-5 text-slate-600" />
            <h3 className="font-heading text-lg font-semibold text-slate-900">Historique des paiements</h3>
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Aucun paiement pour le moment.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Offre</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((t) => (
                  <TableRow key={t.transaction_id}>
                    <TableCell className="font-medium">{t.package_name}</TableCell>
                    <TableCell>{t.amount}€</TableCell>
                    <TableCell>
                      <Badge variant={t.payment_status === "paid" ? "default" : t.payment_status === "failed" ? "destructive" : "secondary"}>
                        {t.payment_status === "paid" ? "Payé" : t.payment_status === "failed" ? "Échoué" : "En attente"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500">{new Date(t.created_at).toLocaleDateString("fr-FR")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}